import React from 'react';

/**
 * `**fett**` in einer einzelnen Zeile auszeichnen.
 *
 * Bewusst die einzige erlaubte Auszeichnung: Die Texte der Website sind vom Kunden
 * bearbeitbar, und alles, was mehr kann als das, landet früher oder später als
 * eingefügtes Word-Markup im ausgelieferten HTML.
 */
const emphasise = (line: string, prefix: string): React.ReactNode[] =>
  line
    .split(/\*\*(.+?)\*\*/g)
    .map((part, index) =>
      index % 2 === 1 ? (
        <strong key={`${prefix}s${index}`}>{part}</strong>
      ) : (
        <React.Fragment key={`${prefix}t${index}`}>{part}</React.Fragment>
      ),
    );

/**
 * Einen Text der Website in Knoten übersetzen: `**fett**`, Zeilenumbrüche und Absätze.
 *
 * Zeilenenden entstehen nur dort, wo der Kunde im Bearbeiten-Modus Enter gedrückt hat —
 * die ausgelieferten Originaltexte sind einzeilig. Ohne diese Übersetzung verschluckt der
 * Browser den Umbruch: Ein `\n` im HTML ist gewöhnlicher Leerraum und wird zu einem
 * Leerzeichen zusammengefasst. Genau das war der Fall, in dem der eingegebene Absatz
 * beim Speichern zwar erhalten blieb, auf der Seite aber nicht zu sehen war.
 *
 * Eine Leerzeile trennt Absätze, eine einzelne Zeile bricht nur um. Beides bleibt
 * **inline**: Die Textstellen sitzen in Überschriften, Absätzen und Listeneinträgen,
 * und ein <p> in einem <p> zerreißt der Browser stillschweigend. Der Abstand kommt
 * deshalb aus einem <span> mit `display: block`, nicht aus einem eigenen Absatz.
 */
export const withEmphasis = (text: string): React.ReactNode[] => {
  const nodes: React.ReactNode[] = [];

  text.split(/\n{2,}/).forEach((block, blockIndex) => {
    if (blockIndex > 0) {
      nodes.push(<span key={`p${blockIndex}`} className="site-absatz" aria-hidden="true" />);
    }
    block.split('\n').forEach((line, lineIndex) => {
      if (lineIndex > 0) nodes.push(<br key={`br${blockIndex}-${lineIndex}`} />);
      nodes.push(...emphasise(line, `${blockIndex}-${lineIndex}-`));
    });
  });

  return nodes;
};

/**
 * Auszeichnung entfernen — für Titel, Beschreibungen und strukturierte Daten.
 *
 * Umbrüche werden dabei zu Leerzeichen: Diese Werte stehen in Attributen und im
 * JSON-LD, wo eine mehrzeilige Angabe nichts verloren hat.
 */
export const withoutEmphasis = (text: string): string =>
  text.replace(/\*\*(.+?)\*\*/g, '$1').replace(/\s*\n+\s*/g, ' ');

export default withEmphasis;
